import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  TooltipItem,
  ChartDataset,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { GRAPH_DEFAULT_OPTIONS } from '../../graphConfig';
import { toLocaleDateTime } from 'src/common/utils/dateUtils';
import { toLocaleBrowserSettings } from 'src/i18n/numberLocale';
import { EmptyLineOrBarChart } from './EmptyLineOrBarChart';
import { useTranslation } from 'react-i18next';

type PercentageLineChartDataset = ChartDataset<'line', number[]> & {
  actualDataValues: number[];
};

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

export type PercentageAreaChartDataset = {
  label: string;
  data: number[];
  actualDataValues: number[];
  borderColor: string;
  backgroundColor: string;
};

type PercentageAreaChartTestProps = {
  datasets: PercentageAreaChartDataset[];
  xAxisLabels: string[];
  tooltipItemPriority?: Record<string, number>;
};

export const PercentageAreaChart = ({
  datasets,
  xAxisLabels,
  tooltipItemPriority,
}: PercentageAreaChartTestProps) => {
  const { t } = useTranslation();

  const isEmptyDataset = datasets.every((set) => set.data.length === 0);

  if (isEmptyDataset) {
    return (
      <EmptyLineOrBarChart
        as={Line}
        emptyChartText={t('common.noResults')}
        xAxisLabels={xAxisLabels}
      />
    );
  }

  const percentageDatasets: PercentageLineChartDataset[] = datasets.map((dataset) => {
    return {
      ...dataset,
      pointRadius: 0,
      pointHoverRadius: 0,
      fill: true,
    };
  });

  const data = {
    labels: xAxisLabels.map((label) => new Date(label)),
    datasets: percentageDatasets,
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        mode: 'index',
        intersect: false,
        itemSort: (a: TooltipItem<'line'>, b: TooltipItem<'line'>) => {
          if (!tooltipItemPriority) {
            return 0;
          }

          const la = tooltipItemPriority[a.dataset.label ?? ''];
          const lb = tooltipItemPriority[b.dataset.label ?? ''];
          return la - lb;
        },
        callbacks: {
          title: (tooltipItems: TooltipItem<'line'>[]) => {
            return toLocaleDateTime(new Date(tooltipItems[0].label));
          },
          label: (tooltipItem: TooltipItem<'line'>) => {
            const { dataset, dataIndex, raw } = tooltipItem;
            const typedDataset = dataset as PercentageLineChartDataset;

            const actualValue = typedDataset.actualDataValues[dataIndex];
            const percentageValue = raw as string;

            return `${dataset.label}: ${toLocaleBrowserSettings(actualValue)} (${Number.parseFloat(
              percentageValue,
            ).toFixed(1)}%)`;
          },
        },
      },
    },
    scales: {
      x: {
        ...GRAPH_DEFAULT_OPTIONS!.scales!.x,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max: 100,
        ticks: {
          display: false,
        },
        grid: {
          drawTicks: false,
        },
      },
    },
    elements: {
      line: { borderWidth: 1 },
    },
  };

  return <Line data={data} options={options} />;
};
